/**
 * Committee Tour
 * Explore legislative committees and their work
 *
 * This tour navigates from /committees to a committee detail page mid-tour.
 * Uses sessionStorage to persist tour state across page navigation.
 */

import type { Tour } from '@/types/onboarding';
import { session } from '@/utils/storage';

const TOUR_RESUME_KEY = 'tour-resume';
const TOUR_RESUME_TTL = 30000; // 30 seconds

interface TourResumeState {
  tourId: string;
  stepIndex: number;
}

/**
 * Type guard for TourResumeState
 */
function isTourResumeState(value: unknown): value is TourResumeState {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.tourId === 'string' && typeof obj.stepIndex === 'number';
}

/**
 * Save tour resume state to sessionStorage
 */
function saveTourResumeState(tourId: string, stepIndex: number) {
  session.set(TOUR_RESUME_KEY, { tourId, stepIndex }, TOUR_RESUME_TTL);
}

/**
 * Get and consume tour resume state from sessionStorage
 * Returns null if not found or expired
 */
function consumeTourResumeState(): TourResumeState | null {
  const state = session.get(TOUR_RESUME_KEY, isTourResumeState);
  if (state) {
    session.remove(TOUR_RESUME_KEY);
  }
  return state;
}

/**
 * Navigate to the House Education committee detail page.
 * Saves tour state so it can resume after navigation.
 */
function navigateToCommitteeDetail(
  _element: Element | undefined,
  _step: unknown,
  { driver }: { driver: { destroy: () => void } }
) {
  // Save state to resume at step 3 (index 2) after navigation
  saveTourResumeState('committee', 2);

  // Stop the current driver instance before navigation
  driver.destroy();

  // Navigate to House Education committee
  window.location.href = '/committees/house-education?state=KS&year=2026';
}

/**
 * Switch tab via custom event and advance to next step after React re-renders.
 * CommitteeDetail listens for 'billbee:switch-tab' events to change tabs.
 */
function switchTabAndAdvance(tabValue: string, driverInstance: { moveNext: () => void }) {
  // Dispatch custom event for CommitteeDetail to handle tab switch
  window.dispatchEvent(new CustomEvent('billbee:switch-tab', { detail: { tab: tabValue } }));

  // Wait for React to re-render tab content, then advance
  setTimeout(() => {
    driverInstance.moveNext();
  }, 250);
}

export const committeeTour: Tour = {
  id: 'committee',
  name: 'Committees',
  description: 'See how bills move through the legislative process',
  startRoute: '/committees',
  steps: [
    // Step 0: Committees list page
    {
      element: '[data-tour="committees-list"]',
      popover: {
        title: 'Where bills live or die',
        description:
          'Most legislation never leaves committee. Understanding committees helps you predict which bills have a real chance.',
        side: 'bottom',
        align: 'start',
      },
    },
    // Step 1: Highlight a committee row, then navigate
    {
      element: '[data-tour="committee-card"]',
      popover: {
        title: 'Pick a committee',
        description: "Click Next to explore the House Education committee's details.",
        side: 'bottom',
        align: 'center',
        onNextClick: navigateToCommitteeDetail,
      },
    },
    // Step 2: Committee detail header (after navigation)
    {
      element: '[data-tour="committee-header"]',
      popover: {
        title: 'Committee details',
        description:
          'Each committee page shows leadership, meeting schedules, and contact information.',
        side: 'bottom',
        align: 'start',
      },
    },
    // Step 3: Tab navigation
    {
      element: '[data-tour="committee-tabs"]',
      popover: {
        title: 'Navigate committee info',
        description:
          'Use these tabs to explore different aspects of the committee: overview, members, and bills.',
        side: 'bottom',
        align: 'center',
      },
    },
    // Step 4: Overview tab content
    {
      element: '[data-tour="committee-overview"]',
      popover: {
        title: 'Committee overview',
        description:
          'The Overview tab shows committee leadership, meeting schedule, location, and staff contacts.',
        side: 'top',
        align: 'center',
        onNextClick: (_el, _step, { driver }) => switchTabAndAdvance('members', driver),
      },
    },
    // Step 5: Members tab (after switching to Members tab)
    {
      element: '[data-tour="committee-members-tab"]',
      popover: {
        title: 'Who decides',
        description:
          "The chair controls what gets heard. Knowing committee membership helps you understand a bill's chances.",
        side: 'bottom',
        align: 'center',
        onNextClick: (_el, _step, { driver }) => switchTabAndAdvance('bills', driver),
      },
    },
    // Step 6: Bills tab (after switching to Bills tab)
    {
      element: '[data-tour="committee-bills-tab"]',
      popover: {
        title: 'Bills in committee',
        description:
          'Track bills assigned to this committee. Most bills die in committee, so this is where the real action happens.',
        side: 'bottom',
        align: 'center',
      },
    },
  ],
};

export { TOUR_RESUME_KEY, saveTourResumeState, consumeTourResumeState, type TourResumeState };
